import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "@/lib/auth";
import { AppLogo } from "@/components/app/Primitives";
import TID from "@/lib/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const DEMO_PASSWORD_ACCOUNTS = [
  { label: "Administrator · L3 (Ashwin Reddy)", email: "areddy@propay.ai" },
  { label: "ProPay Operations (Ravi Kumar)", email: "procurement@propay.ai" },
  { label: "Auditor (EY) · Partner (Rao Krishnan · HUL)", email: "rao.krishnan@ey.example" },
  { label: "Auditor (EY) · Sr Manager (Meera Iyer · HUL + Nestle)", email: "meera.iyer@ey.example" },
  { label: "Auditor (EY) · Manager (Arjun Mehta · HUL)", email: "arjun.mehta@ey.example" },
  { label: "HUL Media Finance · Influencer Audit & Payments (Ananya Desai)", email: "marketing@unilever.example" },
  { label: "HUL Approver · Media Finance Controller (Vikram Sethi)", email: "approver@hul.example" },
  { label: "Nestle · Media Finance (Rohan Mehta)", email: "brand@nestle.example" },
  { label: "Coca-Cola · Media Finance (Kavya Nair)", email: "marketing@cocacola.example" },
];

const DEMO_OTP_ACCOUNTS = [
  { label: "Priya Sharma (verified)", email: "priya.influencer@example.com", mobile: "+91-9876543210" },
  { label: "Arjun Kapoor (verified)", email: "arjun.influencer@example.com", mobile: "+91-9876543211" },
  { label: "Studio Creators LLP (in review)", email: "studio@influencers.example", mobile: "+91-9876543212" },
  { label: "Sundeep Reddy (verified)", email: "sreddy@propay.ai", mobile: "+91-9876543213" },
];

export default function Login() {
  const { login, otpRequest, otpVerify } = useAuth();
  const navigate = useNavigate();

  // password tab state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Propay@2026");
  const [busy, setBusy] = useState(false);

  // OTP tab state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpMobile, setOtpMobile] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [mockCodes, setMockCodes] = useState(null);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name}`);
      navigate(homeForRole(user.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async () => {
    if (!otpEmail || !otpMobile) return toast.error("Enter email and mobile");
    setOtpBusy(true);
    try {
      const data = await otpRequest(otpEmail, otpMobile);
      setOtpSent(true);
      setMockCodes({ email: data.email_otp, phone: data.phone_otp });
      toast.success("OTP sent (mocked — codes shown below)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setOtpBusy(true);
    try {
      const user = await otpVerify({
        email: otpEmail, mobile: otpMobile,
        email_otp: emailOtp, phone_otp: phoneOtp,
      });
      toast.success(`Welcome, ${user.name}`);
      navigate(homeForRole(user.role));
    } catch (err) {
      const detail = err?.response?.data?.detail || "OTP verify failed";
      // Route brand-new emails (no prior Procurement invite) to the signup
      // page so we can collect legal_name / country / type.
      if (/legal_name/i.test(detail)) {
        toast.info("Looks like you're new — please register your details.");
        navigate(`/influencer-signup?email=${encodeURIComponent(otpEmail)}&mobile=${encodeURIComponent(otpMobile)}`);
        return;
      }
      toast.error(detail);
    } finally {
      setOtpBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex marketing-hero p-12 flex-col justify-between">
        <Link to="/"><AppLogo dark /></Link>
        <div>
          <h2 className="font-heading text-4xl font-black leading-tight tracking-tight text-white">
            One <span className="text-gradient-blue">verified vendor</span> for enterprise influencer procurement.
          </h2>
          <p className="text-slate-400 mt-4 max-w-md">
            FMCG customers submit orders. Procurement Desk onboards influencers and issues back-to-back POs from
            PROPAY GLOBAL PRIVATE LIMITED. Influencers deliver work and get paid on time.
          </p>
        </div>
        <div className="text-xs text-slate-500">© 2026 PROPAY GLOBAL PRIVATE LIMITED · Enterprise Influencer Procurement & Payment Network</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden mb-8 inline-block"><AppLogo dark /></Link>
          <h1 className="font-heading text-3xl font-black tracking-tight">Sign in to ProPay Engage360</h1>
          <p className="text-slate-400 mt-2 text-sm">HUL Media Finance, Auditor, ProPay Operations, Admin — password. Creators — OTP.</p>

          <Tabs defaultValue="password" className="mt-8">
            <TabsList className="grid grid-cols-2 bg-white/5 border border-white/10">
              <TabsTrigger data-testid={TID.loginTabPassword} value="password" className="data-[state=active]:bg-[#0052FF] data-[state=active]:text-white">Password</TabsTrigger>
              <TabsTrigger data-testid={TID.loginTabOtp} value="otp" className="data-[state=active]:bg-[#0052FF] data-[state=active]:text-white">Creator / Agency OTP</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-4">
              <form data-testid={TID.loginForm} onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Email</label>
                  <Input data-testid={TID.loginEmail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" />
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Password</label>
                  <Input data-testid={TID.loginPassword} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" />
                </div>
                <Button data-testid={TID.loginSubmit} type="submit" disabled={busy} className="w-full bg-[#0052FF] hover:bg-[#0043D9] text-white h-11 font-semibold">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <div className="mt-8 text-xs text-slate-400 border-t border-white/10 pt-6">
                <div className="mb-2 font-semibold text-slate-300">Demo accounts (password: <code className="text-[#7AA6FF]">Propay@2026</code>)</div>
                <div className="grid grid-cols-1 gap-1.5">
                  {DEMO_PASSWORD_ACCOUNTS.map((a) => (
                    <button key={a.email} type="button" onClick={() => setEmail(a.email)}
                      className="text-left px-2 py-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-white truncate">
                      {a.label}<br /><span className="text-slate-500 text-[10px]">{a.email}</span>
                    </button>
                  ))}
                </div>

                {/* Coupa / One Buy PunchOut demo shortcut */}
                <Link
                  to="/onebuy"
                  data-testid="onebuy-demo-link"
                  className="mt-4 flex items-center justify-between gap-2 rounded-lg border border-[#00A9E0]/40 bg-gradient-to-r from-[#1F36C7]/20 to-[#00A9E0]/10 px-3 py-2.5 hover:from-[#1F36C7]/30 hover:to-[#00A9E0]/20 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded bg-[#1F36C7] text-white font-bold grid place-items-center text-xs">U</div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-[#7AA6FF]">One Buy · Coupa</div>
                      <div className="text-xs text-slate-200 leading-tight">See how Engage360 works via One Buy →</div>
                    </div>
                  </div>
                  <span className="text-[#7AA6FF] font-black">↗</span>
                </Link>
              </div>
            </TabsContent>

            <TabsContent value="otp" className="mt-4">
              {!otpSent ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Email</label>
                    <Input data-testid={TID.otpEmailInput} type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="you@example.com" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Mobile</label>
                    <Input data-testid={TID.otpMobileInput} value={otpMobile} onChange={(e) => setOtpMobile(e.target.value)} placeholder="+91-9876543210" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11" />
                  </div>
                  <Button data-testid={TID.otpRequestBtn} onClick={requestOtp} disabled={otpBusy} className="w-full bg-[#0052FF] hover:bg-[#0043D9] text-white h-11 font-semibold">
                    {otpBusy ? "Sending…" : "Send OTP to email + mobile"}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    New influencer? <Link to="/influencer-signup" className="text-[#7AA6FF] hover:underline">Register in 60 seconds →</Link>
                  </p>

                  <div className="mt-6 text-xs text-slate-400 border-t border-white/10 pt-4">
                    <div className="mb-2 font-semibold text-slate-300">Demo influencers</div>
                    <div className="grid gap-1.5">
                      {DEMO_OTP_ACCOUNTS.map((a) => (
                        <button key={a.email} type="button" onClick={() => { setOtpEmail(a.email); setOtpMobile(a.mobile); }}
                          className="text-left px-2 py-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-white truncate">
                          {a.label}<br /><span className="text-slate-500 text-[10px]">{a.email} · {a.mobile}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4">
                  {mockCodes && (
                    <div className="text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-lg p-3">
                      <div className="font-bold uppercase tracking-widest mb-1">Demo mode — auto-filled codes</div>
                      Email OTP: <code className="font-mono">{mockCodes.email}</code> · Phone OTP: <code className="font-mono">{mockCodes.phone}</code>
                      <button type="button" className="ml-2 underline" onClick={() => { setEmailOtp(mockCodes.email); setPhoneOtp(mockCodes.phone); }}>Auto-fill</button>
                    </div>
                  )}
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Email OTP</label>
                    <Input data-testid={TID.otpEmailCodeInput} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} maxLength={6} placeholder="6-digit code" className="bg-white/5 border-white/10 text-white h-11 tracking-widest font-mono" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold text-slate-400 tracking-wider mb-1.5 block">Phone OTP</label>
                    <Input data-testid={TID.otpPhoneCodeInput} value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} maxLength={6} placeholder="6-digit code" className="bg-white/5 border-white/10 text-white h-11 tracking-widest font-mono" />
                  </div>
                  <Button data-testid={TID.otpVerifyBtn} type="submit" disabled={otpBusy} className="w-full bg-[#0052FF] hover:bg-[#0043D9] text-white h-11 font-semibold">
                    {otpBusy ? "Verifying…" : "Verify & sign in"}
                  </Button>
                  <button type="button" onClick={() => { setOtpSent(false); setEmailOtp(""); setPhoneOtp(""); }} className="text-xs text-slate-400 hover:text-white underline w-full">
                    ← Change email / mobile
                  </button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
